// Added by Sejune Oh on 2019-04-05
// Copyright (c) Coreline Soft, All rights are reserved.
//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
import { makeAutoObservable } from 'mobx'
import moment from 'moment'

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// Logger
import Logger from '@/helper/Logger'

// Root store
import Rootstore from '../../index'

// Store
import {
    reqGetLicense,
    reqGetMachineInfo,
    reqRegisterLicense,
    reqUnregisterLicense,
    reqUpdateLicenseProps,
    reqCheckLicenseServerConnection,
} from './LicenseStore.service'

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
// Interface
export interface LicenseProps {
    status?: 'activated' | 'deactivated'
    license_type?: 'timelimited' | 'permanent'
    version?: string
    license_number?: string
    serial_key?: string
    max_app_session_count?: string
    max_data_storage_count?: string
    max_user_session_node_count?: string
    max_aps_session_node_count?: string
    expired_at?: string
    end_of_support_at?: string
    license_keys?: Array<string>
    searchable_license_keys?: { [key: string]: boolean }
    license_data_base64?: string
    encrypted_machine_info_base64?: string
}

//////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
export default class LicenseStore {
    licenseInfo?: LicenseProps
    machineInfo?: string
    licenseFeatureKeys: { [key: string]: Array<string> } = {}

    root?: Rootstore

    //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
    constructor(root: Rootstore) {
        this.root = root
        makeAutoObservable(this)
    }

    //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
    get expired() {
        if (!this.isValidLicense) return true
        if (this.licenseInfo!.license_type === 'permanent') return false

        return moment(Date.now()).isAfter(this.licenseInfo!.expired_at)
    }

    get licenseNumber() {
        if (!this.isValidLicense) return ''

        return this.licenseInfo!.license_number!
    }

    get licenseType() {
        if (!this.isValidLicense) return ''

        return this.licenseInfo!.license_type!
    }

    get expiredAt() {
        if (!this.isValidLicense) return ''

        return this.licenseInfo!.expired_at!
    }

    get endOfSupportAt() {
        if (!this.isValidLicense) return ''

        return this.licenseInfo!.end_of_support_at!
    }

    get maxUserSessionCount() {
        if (!this.isValidLicense) return 0

        return parseInt(this.licenseInfo!.max_app_session_count!)
    }

    get maxDataStorageCount() {
        if (!this.isValidLicense) return 0

        return parseInt(this.licenseInfo!.max_data_storage_count!)
    }

    get maxUserSessionNodeCount() {
        if (!this.isValidLicense) return 0

        return parseInt(this.licenseInfo!.max_user_session_node_count!)
    }

    get maxApsSessionNodeCount() {
        if (!this.isValidLicense) return 0

        return parseInt(this.licenseInfo!.max_aps_session_node_count!)
    }

    get licenseKeys() {
        if (!this.isValidLicense) return ''

        return this.licenseInfo!.license_keys!
    }

    get isValidLicense() {
        return (
            !!this.licenseInfo &&
            this.licenseInfo!.license_keys &&
            Object.keys(this.licenseInfo!.license_keys!).length > 0
        )
    }

    get isFakeLicense() {
        if (!this.isValidLicense) return false

        return this.licenseNumber === '000000000'
    }

    //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
    _setLicenseInfo = (licenseInfo: LicenseProps) => {
        const licenseFeatureKeys: { [key: string]: string[] } = {}
        const licenseKeys: { [key: string]: boolean } = {}

        if (!licenseInfo.license_keys) {
            licenseInfo.license_keys = []
            licenseInfo.searchable_license_keys = licenseKeys
        } else {
            licenseInfo.license_keys.forEach((key) => {
                licenseKeys[key!] = true
                const [groupName, ...featureName] = key.split('.')
                if (licenseFeatureKeys[groupName]) {
                    licenseFeatureKeys[groupName].push(featureName.join('.'))
                } else {
                    licenseFeatureKeys[groupName] = [featureName.join('.')]
                }
            })
        }

        this.licenseInfo = licenseInfo
        this.licenseInfo.searchable_license_keys = licenseKeys
        this.licenseFeatureKeys = licenseFeatureKeys
    }

    _setMachineInfo = (machineInfo: string) => {
        this.machineInfo = machineInfo
    }

    //////////////////////////////////////////////////////////////////////////////////////////////////////////////////////
    getLicenseInfo = async () => {
        try {
            const { data } = await reqGetLicense()
            this._setLicenseInfo(data)
            Logger.devLog('License info has been loaded successfully.')
        } catch (err) {
            Logger.errLog('Can\'t get license info.')
        }
    }

    getMachineInfo = async () => {
        if (this.machineInfo) {
            return this.machineInfo
        }

        const {
            data: { encrypted_machine_info_base64: machineInfo },
        } = await reqGetMachineInfo()
        this._setMachineInfo(machineInfo as string)
        return machineInfo
    }

    registerLicense = async ({
        serial_key = '',
        license_data_base64 = '',
    }: {
        serial_key?: string
        license_data_base64?: string
    }) => {
        return await reqRegisterLicense({
            serial_key,
            license_data_base64,
        })
    }

    unregisterLicense = async () => {
        await reqUnregisterLicense()
        this._setLicenseInfo({})
        return
    }

    updateLicense = async () => {
        return await reqUpdateLicenseProps()
    }

    checkLicenseFeature = (key: string) => {
        if (!this.isValidLicense) return false
        return this.licenseInfo!.searchable_license_keys![key] || false
    }

    // Check connection to license server
    reqCheckLicenseServerConnection = async () => {
        try {
            await reqCheckLicenseServerConnection()
            return true
        } catch (err) {
            return false
        }
    }
}
